
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { format } from "date-fns";
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDeals, fetchFilteredStageCounts } from "@/hooks/useDeals";
import { useDealsSearch } from "@/hooks/useDealsSearch";
import { useCurrentAppUser } from "@/hooks/useCurrentAppUser";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Filter, CalendarIcon, X, User as UserIcon, Building2, CheckSquare } from "lucide-react";
import { BusinessUnitSelector } from '@/components/finance/BusinessUnitSelector';
import { toast } from "sonner";

import { createPageUrl } from "@/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { SOURCE_TYPES, CHANNEL_TYPES } from "@/lib/sourceChannelTypes";

import DealForm from "../components/deals/DealForm";
import DealKanban from "../components/deals/DealKanban";
import BulkActionBar from "@/components/common/BulkActionBar";
import ListModal from "@/components/lists/ListModal";
import { ActiveFilters } from "@/components/common/ActiveFilters";
import { MultiSelect } from "@/components/ui/multi-select";
import { User } from "@/api/entities";
import { useScheduledCallsByDeal } from "@/hooks/useScheduledCallsByDeal";
import ProspectingValidationModal from "@/components/deals/ProspectingValidationModal";
import { validateProspectingRequirements } from "@/lib/prospectingValidation";

// SDR and Consultant role IDs
const SDR_ROLE_ID = 'c40b5988-bc87-4997-bbf8-94beccd9f57c';
const CONSULTANT_ROLE_ID = 'a1263bf1-a38d-41d1-838f-f78d76b59eb9';
const SALES_MANAGER_ROLE_ID = 'be224e11-513f-496e-90aa-0530d3a94789';

export default function Deals() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use centralized user hook (cached via React Query)
  const { data: currentAppUser, isLoading: userLoading } = useCurrentAppUser();
  
  // Permissions hook for bulk delete
  const { isAdmin, isSalesManager, isLoading: permissionsLoading } = useUserPermissions();
  const canBulkDelete = !permissionsLoading && (isAdmin || isSalesManager);
  
  // Pre-set columns filter to "ativas" (active) to avoid loading closed deals
  const [kanbanColumnsFilter, setKanbanColumnsFilter] = useState("ativas");
  
  const [appUsers, setAppUsers] = useState([]);
  const [deduplicatedUsers, setDeduplicatedUsers] = useState([]);
  const [userGroupMap, setUserGroupMap] = useState({});
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [initialContactId, setInitialContactId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const isCreatingDealRef = useRef(false);
  
  const [moduleSettings, setModuleSettings] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [customDateRange, setCustomDateRange] = useState(null);
  const [sortBy, setSortBy] = useState("created_date_desc");
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [closingDateRange, setClosingDateRange] = useState(null);
  const [sdrFilter, setSdrFilter] = useState([]);
  const [consultantFilter, setConsultantFilter] = useState([]);
  const [businessUnitFilter, setBusinessUnitFilter] = useState("all");
  
  // Users by role
  const [sdrUsers, setSdrUsers] = useState([]);
  const [consultantUsers, setConsultantUsers] = useState([]);

  // Selection state
  const [selectedDealIds, setSelectedDealIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Filtered stage counts (for accurate totals when filters are active)
  const [filteredStageCounts, setFilteredStageCounts] = useState({});

  // Prospecting validation modal state
  const [showProspectingValidation, setShowProspectingValidation] = useState(false);
  const [prospectingValidationData, setProspectingValidationData] = useState({ dealId: '', contactId: '', missingFields: { contact: [], deal: [] } });

  // Bulk delete state
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // List modal state
  const [showListModal, setShowListModal] = useState(false);

  // Calculate assignedTo filter for server-side filtering
  const assignedToFilter = React.useMemo(() => {
    if (assignedFilter === "all") return undefined;
    if (assignedFilter === "unassigned") return "unassigned";
    if (assignedFilter === "my_deals") return currentAppUser?.id;
    
    // Use all IDs from the deduplicated group
    const idsToMatch = userGroupMap[assignedFilter] || [assignedFilter];
    return idsToMatch;
  }, [assignedFilter, userGroupMap, currentAppUser]);

  // Pass stage filter and assignedTo to useDeals hook for server-side filtering
  const hookStageFilter = kanbanColumnsFilter === "ativas" ? "active" : 
                      kanbanColumnsFilter === "nao_ativas" ? "closed" : "all";
  
  // Compute createdFrom/createdTo from dateFilter for server-side filtering
  const { createdFrom, createdTo } = React.useMemo(() => {
    if (dateFilter === "all") return { createdFrom: null, createdTo: null };
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    if (dateFilter === "today") {
      return { createdFrom: today.toISOString(), createdTo: null };
    } else if (dateFilter === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const endOfYesterday = new Date(today.getTime() - 1);
      return { createdFrom: yesterday.toISOString(), createdTo: endOfYesterday.toISOString() };
    } else if (dateFilter === "this_week") {
      const firstDayOfWeek = new Date(today);
      firstDayOfWeek.setDate(today.getDate() - today.getDay());
      return { createdFrom: firstDayOfWeek.toISOString(), createdTo: null };
    } else if (dateFilter === "this_month") {
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { createdFrom: firstDayOfMonth.toISOString(), createdTo: null };
    } else if (dateFilter === "custom" && customDateRange?.from) {
      const fromDate = new Date(customDateRange.from);
      const startOfDay = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
      if (customDateRange.to) {
        const toDate = new Date(customDateRange.to);
        const endOfDay = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 59, 59);
        return { createdFrom: startOfDay.toISOString(), createdTo: endOfDay.toISOString() };
      } else {
        const endOfDay = new Date(startOfDay);
        endOfDay.setDate(endOfDay.getDate() + 1);
        return { createdFrom: startOfDay.toISOString(), createdTo: new Date(endOfDay.getTime() - 1).toISOString() };
      }
    }
    return { createdFrom: null, createdTo: null };
  }, [dateFilter, customDateRange]);

  const { 
    deals, 
    totalCount, 
    loading, 
    loadingMore, 
    backgroundProgress, 
    stageServerCounts, 
    loadDeals, 
    loadMoreForStage, 
    loadMoreDeals,
    invalidateCache,
    // New per-stage state
    dealsByStage,
    stageCounts,
    hasMoreByStage,
    loadingMoreStage
  } = useDeals({
    stageFilter: hookStageFilter,
    assignedTo: assignedToFilter,
    sdrIds: sdrFilter.length > 0 ? sdrFilter : undefined,
    consultantIds: consultantFilter.length > 0 ? consultantFilter : undefined,
    enabled: filtersLoaded, // Only start query when user preferences are loaded
    closingDateFrom: closingDateRange?.from ? safeFormat(new Date(closingDateRange.from), 'yyyy-MM-dd') : null,
    closingDateTo: closingDateRange?.to ? safeFormat(new Date(closingDateRange.to), 'yyyy-MM-dd') : closingDateRange?.from ? safeFormat(new Date(closingDateRange.from), 'yyyy-MM-dd') : null,
    createdFrom,
    createdTo,
    sourceFilter: sourceFilter !== 'all' ? sourceFilter : null,
    channelFilter: channelFilter !== 'all' ? channelFilter : null,
    businessUnitFilter: businessUnitFilter !== 'all' ? businessUnitFilter : null,
  });
  const isLoading = loading;
  
  // Hook para busca server-side de deals (busca em TODOS os deals do banco)
  const { 
    results: searchResults, 
    loading: searchLoading, 
    isSearching 
  } = useDealsSearch(searchQuery, { enabled: filtersLoaded });
  
  // Hook para buscar calls agendadas por deal
  const { scheduledCallsByDeal } = useScheduledCallsByDeal();

  // 🐛 DEBUG: Verificar deals recebidos
  useEffect(() => {
    if (deals.length > 0) {
      const dealComUsuario = deals.find(d => d.sales_consultant || d.sales_development);
      console.log('🔍 [Deals.jsx] Primeiro deal com usuário:', dealComUsuario);
      if (dealComUsuario) {
        console.log('🔍 [Deals.jsx] sold_by:', dealComUsuario.sold_by);
        console.log('🔍 [Deals.jsx] sales_dev:', dealComUsuario.sales_dev);
      }
    }
  }, [deals]);

  // Client-side only filters (controls sentinel/infinite scroll behavior)
  const hasClientSideFilters = closingDateRange !== null;
  
  // All filters including server-side (for UI: clear button, ActiveFilters component)
  const hasActiveFilters = hasClientSideFilters || assignedFilter !== "all" || sdrFilter.length > 0 || consultantFilter.length > 0 || dateFilter !== "all" || sourceFilter !== "all" || channelFilter !== "all" || businessUnitFilter !== "all";
  
  // Fetch filtered stage counts when filters are active
  useEffect(() => {
    if (hasActiveFilters && filtersLoaded) {
      console.log('📊 [Deals] Fetching filtered stage counts...');
      fetchFilteredStageCounts({
        sdrIds: sdrFilter.length > 0 ? sdrFilter.filter(s => s !== 'unassigned') : undefined,
        consultantIds: consultantFilter.length > 0 ? consultantFilter.filter(c => c !== 'unassigned') : undefined,
        sdrIncludeUnassigned: sdrFilter.includes('unassigned'),
        consultantIncludeUnassigned: consultantFilter.includes('unassigned'),
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
        channel: channelFilter !== 'all' ? channelFilter : undefined,
        businessUnit: businessUnitFilter !== 'all' ? businessUnitFilter : undefined,
        stageFilter: hookStageFilter,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined
      }).then(counts => {
        console.log('📊 [Deals] Filtered stage counts:', counts);
        setFilteredStageCounts(counts);
      }).catch(err => {
        console.error('❌ [Deals] Error fetching filtered stage counts:', err);
      });
    } else {
      // Clear filtered counts when no filters active
      setFilteredStageCounts({});
    }
  }, [hasActiveFilters, sdrFilter, consultantFilter, sourceFilter, channelFilter, businessUnitFilter, hookStageFilter, filtersLoaded, createdFrom, createdTo]);
  
  const clearAllFilters = () => {
    setAssignedFilter("all");
    setDateFilter("all");
    setCustomDateRange(null);
    setSearchQuery("");
    setSourceFilter("all");
    setChannelFilter("all");
    setClosingDateRange(null);
    setSdrFilter([]);
    setConsultantFilter([]);
    setBusinessUnitFilter("all");
  };

  // Deduplicate users and create ID groups mapping
  const deduplicateUsers = useCallback((users) => {
    const groups = new Map();
    
    users.forEach(user => {
      const email = user.email?.toLowerCase();
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
      
      // Normalize email removing +OLD suffix
      const normalizedEmail = email?.replace(/\+old@/i, '@');
      
      // Create unique key (prefer normalized email, fallback to name)
      const key = normalizedEmail || fullName || `user_${user.id}`;
      
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(user);
    });
    
    // For each group, prioritize:
    // 1) Users with auth_user_id (active versions)
    // 2) Users WITHOUT "(OLD)" in name
    // 3) Most recent
    const deduplicatedUsers = [];
    const idGroupMap = {};
    
    groups.forEach((group) => {
      const sorted = group.sort((a, b) => {
        // Priority 1: has auth_user_id
        if (a.auth_user_id && !b.auth_user_id) return -1;
        if (!a.auth_user_id && b.auth_user_id) return 1;
        
        // Priority 2: does NOT have "(OLD)" in name
        const aIsOld = a.display_name?.includes('OLD') || a.email?.includes('+OLD');
        const bIsOld = b.display_name?.includes('OLD') || b.email?.includes('+OLD');
        if (!aIsOld && bIsOld) return -1;
        if (aIsOld && !bIsOld) return 1;
        
        // Priority 3: most recent
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      
      const representative = sorted[0];
      deduplicatedUsers.push(representative);
      
      // Store all IDs from this group
      idGroupMap[representative.id] = group.map(u => u.id);
    });
    
    // Sort alphabetically
    deduplicatedUsers.sort((a, b) => {
      const nameA = a.display_name || `${a.first_name} ${a.last_name}`.trim() || a.email;
      const nameB = b.display_name || `${b.first_name} ${b.last_name}`.trim() || b.email;
      return nameA.localeCompare(nameB);
    });
    
    return { deduplicatedUsers, idGroupMap };
  }, []);

  const loadData = async () => {
    try {
      // OPTIMIZED: Single query for all users with minimal fields
      // Contact data is already included via JOIN in useDeals hook (LIST_SELECT)
      const { data: usersData } = await supabase
        .from('app_users')
        .select('id, display_name, first_name, last_name, email, system_role_id, auth_user_id, created_at, active')
        .order('display_name');
      
      if (usersData) {
        setAppUsers(usersData);
        
        // Filter SDR and Consultant users client-side (57 users total - very fast)
        const sdrData = usersData.filter(u => u.system_role_id === SDR_ROLE_ID && u.active);
        const consultantData = usersData.filter(u => (u.system_role_id === CONSULTANT_ROLE_ID || u.system_role_id === SALES_MANAGER_ROLE_ID) && u.active);
        
        setSdrUsers(sdrData);
        setConsultantUsers(consultantData);
        
        // Deduplicate users and create ID groups
        const { deduplicatedUsers, idGroupMap } = deduplicateUsers(usersData);
        setDeduplicatedUsers(deduplicatedUsers);
        setUserGroupMap(idGroupMap);
      }
      
      // Set module settings from cached user
      if (currentAppUser?.metadata) {
        setModuleSettings(currentAppUser.metadata);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const getAssignedUserName = useCallback((assignedTo) => {
    if (!assignedTo) return null;
    const user = appUsers.find(u => u.id === assignedTo || u.email === assignedTo);
    if (!user) return null;
    const name = user.display_name || [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    return name || (user.email ? user.email.split('@')[0] : 'Unknown');
  }, [appUsers]);

  const filterDeals = useCallback(() => {
    console.log('🟢 [filterDeals] Iniciando filtragem:', {
      dealsRecebidos: deals.length,
      searchQuery,
      isSearching,
      searchResultsCount: searchResults.length,
      assignedFilter,
      dateFilter,
      kanbanColumnsFilter,
      sortBy,
      sourceFilter,
      channelFilter
    });

    // IMPORTANTE: Se está buscando (query >= 2 chars), usar resultados do server-side search
    // Isso permite encontrar deals WON/LOST antigos que não foram carregados inicialmente
    let filtered = isSearching ? [...searchResults] : [...deals];

    // Apply search filter (client-side) ONLY if NOT using server-side search
    // Server-side search already filtered by the query
    if (searchQuery.trim() && !isSearching) {
      // Fallback para busca com apenas 1 caractere (muito curto para server-side)
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(deal => {
        const contactName = deal.contact 
          ? `${deal.contact.first_name || ''} ${deal.contact.last_name || ''}`.toLowerCase()
          : "";
        
        return (
          ((deal.name || deal.deal_name || "").toLowerCase().includes(query)) ||
          contactName.includes(query) ||
          deal.case_type?.toLowerCase().includes(query) ||
          deal.service_type?.toLowerCase().includes(query)
        );
      });
      console.log('🟡 Após filtro de busca (client-side):', filtered.length);
    }

    if (isSearching) {
      console.log('🔍 Usando busca server-side:', searchResults.length, 'resultados');
    }

    // Note: assignedFilter is now handled server-side in useDeals hook
    // Note: sourceFilter, channelFilter, businessUnitFilter are now handled server-side in useDeals hook

    // Date filter is now handled server-side in useDeals hook (createdFrom/createdTo)

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "created_date_desc":
          return new Date(b.created_at) - new Date(a.created_at);
        case "created_date_asc":
          return new Date(a.created_at) - new Date(b.created_at);
        case "name_asc": {
          const nameA = a.name || a.deal_name || "";
          const nameB = b.name || b.deal_name || "";
          return nameA.localeCompare(nameB);
        }
        case "name_desc": {
          const nameA = a.name || a.deal_name || "";
          const nameB = b.name || b.deal_name || "";
          return nameB.localeCompare(nameA);
        }
        case "value_desc": {
          const valueA = a.total_value || a.total_amount || 0;
          const valueB = b.total_value || b.total_amount || 0;
          return valueB - valueA;
        }
        case "value_asc": {
          const valueA = a.total_value || a.total_amount || 0;
          const valueB = b.total_value || b.total_amount || 0;
          return valueA - valueB;
        }
        default:
          return 0;
      }
    });

    console.log('🟢 [filterDeals] Resultado final:', filtered.length);
    setFilteredDeals(filtered);
  }, [searchQuery, deals, assignedFilter, dateFilter, customDateRange, sortBy, sdrFilter, consultantFilter, isSearching, searchResults]);

  useEffect(() => {
    loadData();
  }, []);

  // Handle URL parameters for auto-opening form with pre-selected contact
  useEffect(() => {
    const action = searchParams.get('action');
    const contactId = searchParams.get('contactId');
    
    if (action === 'create') {
      setShowForm(true);
      if (contactId) {
        setInitialContactId(contactId);
      }
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Load saved filter preferences from currentAppUser (cached via React Query)
  // CONSOLIDATED: Single useEffect handles both preference loading AND safety timeout
  useEffect(() => {
    // If already loaded, skip
    if (filtersLoaded) return;
    
    // If user data is available, apply preferences and mark as loaded IMMEDIATELY
    if (currentAppUser) {
      const savedFilters = currentAppUser.metadata?.deals?.filters;
      if (savedFilters) {
        
        if (savedFilters.assignedFilter) setAssignedFilter(savedFilters.assignedFilter);
        if (savedFilters.dateFilter) setDateFilter(savedFilters.dateFilter);
        if (savedFilters.sortBy) setSortBy(savedFilters.sortBy);
        
        // Restore kanbanColumnsFilter (Ativo/Inativo) - safe now because hookStageFilter syncs correctly
        if (savedFilters.kanbanColumnsFilter) setKanbanColumnsFilter(savedFilters.kanbanColumnsFilter);
        
        if (savedFilters.searchQuery) setSearchQuery(savedFilters.searchQuery);
        if (savedFilters.sdrFilter) setSdrFilter(Array.isArray(savedFilters.sdrFilter) ? savedFilters.sdrFilter : []);
        if (savedFilters.consultantFilter) setConsultantFilter(Array.isArray(savedFilters.consultantFilter) ? savedFilters.consultantFilter : []);
        if (savedFilters.businessUnitFilter) setBusinessUnitFilter(savedFilters.businessUnitFilter);
        if (savedFilters.sourceFilter) setSourceFilter(savedFilters.sourceFilter);
        if (savedFilters.channelFilter) setChannelFilter(savedFilters.channelFilter);
        if (savedFilters.closingDateRange) {
          setClosingDateRange({
            from: savedFilters.closingDateRange.from ? new Date(savedFilters.closingDateRange.from) : undefined,
            to: savedFilters.closingDateRange.to ? new Date(savedFilters.closingDateRange.to) : undefined
          });
        }
      }
      // Mark as loaded SYNCHRONOUSLY (no setTimeout) to prevent race conditions
      setFiltersLoaded(true);
      console.log('✅ [Deals] filtersLoaded = true (user loaded)');
      return;
    }
    
    // Safety timeout: if user takes too long to load, proceed with defaults
    const timeout = setTimeout(() => {
      if (!filtersLoaded) {
        console.log('⚠️ [Deals] Timeout: filtersLoaded forçado após 500ms');
        setFiltersLoaded(true);
      }
    }, 500);
    
    return () => clearTimeout(timeout);
  }, [currentAppUser, filtersLoaded]);

  // Handle URL parameters for creating deal with pre-selected contact
  useEffect(() => {
    const action = searchParams.get('action');
    const contactId = searchParams.get('contactId');
    
    if (action === 'create') {
      if (contactId) {
        setInitialContactId(contactId);
      }
      setShowForm(true);
      
      // Clear URL params to prevent re-opening modal on refresh
      searchParams.delete('action');
      searchParams.delete('contactId');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Save filter preferences with debounce
  useEffect(() => {
    if (!currentAppUser?.id || !filtersLoaded) return;
    
    const timer = setTimeout(async () => {
      const updatedSettings = {
        ...currentAppUser.metadata,
        deals: {
          ...currentAppUser.metadata?.deals,
          filters: {
            assignedFilter,
            dateFilter,
            sortBy,
            kanbanColumnsFilter,
            searchQuery,
            sdrFilter,
            consultantFilter,
            businessUnitFilter,
            sourceFilter,
            channelFilter,
            closingDateRange: closingDateRange ? {
              from: closingDateRange.from?.toISOString() || null,
              to: closingDateRange.to?.toISOString() || null
            } : null
          }
        }
      };
      
      try {
        await User.updateMyUserData({ metadata: updatedSettings });
      } catch (error) {
        console.error("Error saving filter preferences:", error);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [assignedFilter, dateFilter, sortBy, kanbanColumnsFilter, searchQuery, sdrFilter, consultantFilter, businessUnitFilter, sourceFilter, channelFilter, closingDateRange, currentAppUser?.id, filtersLoaded]);

  // Update deduplicated users when appUsers change
  useEffect(() => {
    if (appUsers.length > 0) {
      const { deduplicatedUsers, idGroupMap } = deduplicateUsers(appUsers);
      setDeduplicatedUsers(deduplicatedUsers);
      setUserGroupMap(idGroupMap);
    }
  }, [appUsers, deduplicateUsers]);

  useEffect(() => {
    filterDeals();
  }, [filterDeals]);

  const handleCreateDeal = async (dealData, catalogId) => {
    if (isCreatingDealRef.current) return;
    isCreatingDealRef.current = true;
    setIsProcessing(true);
    try {
      const { data, error } = await supabase
        .from('deals')
        .insert([dealData])
        .select()
        .single();
      
      if (error) throw error;
      
      // If a catalog item was selected, create an item record
      if (catalogId) {
        const { data: catalogItem } = await supabase
          .from('catalog')
          .select('*')
          .eq('id', catalogId)
          .single();
        
        if (catalogItem) {
          const price = Number(catalogItem.base_price) || 0;
          await supabase
            .from('items')
            .insert([{
              deal_id: data.id,
              catalog_id: catalogId,
              quantity: 1,
              unit_price: price,
              subtotal: price,
              total_amount: price,
              status: 'proposed'
            }]);
        }
      }
      
      setShowForm(false);
      
      // Redirect to deal detail page
      window.location.href = createPageUrl("DealDetail") + "?id=" + data.id;
    } catch (error) {
      console.error("Error creating deal:", error);
      alert("Error creating deal: " + (error.message || "Unknown error"));
    }
    isCreatingDealRef.current = false;
    setIsProcessing(false);
  };

  const handleViewDeal = (dealId) => {
    window.location.href = createPageUrl("DealDetail") + "?id=" + dealId;
  };

  const handleStageChange = async (deal, newStage, options = {}) => {
    const { skipUpdate = false } = options;
    
    try {
      // Se skipUpdate=true, apenas recarregar dados (update já foi feito pelo modal)
      if (skipUpdate) {
        invalidateCache(); // Limpar cache para forçar refetch
        await loadDeals();
        return;
      }

      // Validate: PROSPECTING requires contact and deal fields
      if (newStage === "PROSPECTING") {
        // Fetch FRESH contact with addresses (must specify relationship to avoid ambiguity)
        const { data: contactWithAddresses, error: contactError } = await supabase
          .from('contacts')
          .select('*, contact_addresses:contact_addresses!contact_addresses_contact_id_fkey(*)')
          .eq('id', deal.contact_id)
          .single();
        
        if (contactError) {
          console.error('Erro ao buscar contato:', contactError);
          toast.error('Erro ao validar dados do contato');
          return;
        }
        
        // Fetch FRESH deal data (the deal object from Kanban may be stale)
        const { data: freshDeal, error: dealError } = await supabase
          .from('deals')
          .select('*')
          .eq('id', deal.id)
          .single();
        
        if (dealError) {
          console.error('Erro ao buscar deal:', dealError);
          toast.error('Erro ao validar dados do deal');
          return;
        }
        
        const addresses = contactWithAddresses?.contact_addresses || [];
        // Use freshDeal instead of stale deal object
        const validation = validateProspectingRequirements(contactWithAddresses, freshDeal, addresses);
        
        if (!validation.isValid) {
          // Reload deals to revert visual state
          await loadDeals();
          // Show modal with missing fields
          setProspectingValidationData({
            dealId: deal.id,
            contactId: deal.contact_id,
            missingFields: validation.missingFields
          });
          setShowProspectingValidation(true);
          return;
        }
      }

      const updateData = {
        stage: newStage
      };

      if (newStage === "WON" || newStage === "LOST" || newStage === "won" || newStage === "lost") {
        updateData.closed_date = new Date().toISOString().split('T')[0];
      }

      await supabase
        .from('deals')
        .update(updateData)
        .eq('id', deal.id);
      
      invalidateCache(); // Invalidar cache antes de recarregar
      await loadDeals();
    } catch (error) {
      console.error("Error updating deal stage:", error);
      toast.error('Erro ao atualizar estágio do deal');
    }
  };

  // Selection functions
  const toggleSelectDeal = (dealId) => {
    setSelectedDealIds(prev => 
      prev.includes(dealId) 
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  const clearSelection = () => {
    setSelectedDealIds([]);
  };

  const handleRemoveFilter = (filterKey, value) => {
    if (filterKey === 'all') {
      setSearchQuery('');
      setAssignedFilter('all');
      setDateFilter('all');
      setKanbanColumnsFilter('all');
      setSourceFilter('all');
      setChannelFilter('all');
      setClosingDateRange(null);
      setSdrFilter([]);
      setConsultantFilter([]);
      setBusinessUnitFilter('all');
      toast.success(t('common.allFiltersRemoved'));
      return;
    }
    
    switch (filterKey) {
      case 'searchQuery':
        setSearchQuery('');
        break;
      case 'assignedFilter':
        setAssignedFilter('all');
        break;
      case 'dateFilter':
        setDateFilter('all');
        break;
      case 'kanbanColumnsFilter':
        setKanbanColumnsFilter('all');
        break;
      case 'sourceFilter':
        setSourceFilter('all');
        break;
      case 'channelFilter':
        setChannelFilter('all');
        break;
      case 'closingDateRange':
        setClosingDateRange(null);
        break;
      case 'sdrFilter':
        if (value) {
          setSdrFilter(prev => prev.filter(v => v !== value));
        } else {
          setSdrFilter([]);
        }
        break;
      case 'consultantFilter':
        if (value) {
          setConsultantFilter(prev => prev.filter(v => v !== value));
        } else {
          setConsultantFilter([]);
        }
        break;
      case 'businessUnitFilter':
        setBusinessUnitFilter('all');
        break;
    }
  };

  // Bulk action type state
  const [bulkActionType, setBulkActionType] = React.useState('stage');

  const handleBulkStageChange = async (newStage) => {
    if (selectedDealIds.length === 0) return;
    
    try {
      const updateData = {
        stage: newStage
      };

      if (newStage === "closed_won" || newStage === "closed_lost") {
        updateData.closed_date = new Date().toISOString().split('T')[0];
      }

      const { error } = await supabase
        .from('deals')
        .update(updateData)
        .in('id', selectedDealIds);
      
      if (error) throw error;

      toast.success(`${selectedDealIds.length} ${t('deals.title').toLowerCase()} atualizados com sucesso.`);
      
      clearSelection();
      await loadDeals();
    } catch (e) {
      console.error('Error updating deals in bulk:', e);
      toast.error(e?.message || t('messages.errorUpdating'));
    }
  };

  const handleBulkSdrChange = async (rawSdrId) => {
    if (selectedDealIds.length === 0) return;
    const newSdrId = rawSdrId === '__unassigned__' ? null : rawSdrId;
    
    try {
      // Get current SDR values for logging
      const { data: currentDeals } = await supabase
        .from('deals')
        .select('id, sales_development')
        .in('id', selectedDealIds);

      const { error } = await supabase
        .from('deals')
        .update({ sales_development: newSdrId })
        .in('id', selectedDealIds);
      
      if (error) throw error;

      // Log each assignment change
      if (currentDeals) {
        const logs = currentDeals
          .filter(d => (d.sales_development || null) !== newSdrId)
          .map(d => ({
            deal_id: d.id,
            from_user_id: d.sales_development || null,
            to_user_id: newSdrId,
            target_field: 'sales_development',
            reason: 'bulk_reassign',
            changed_by: currentAppUser?.id || null,
          }));
        if (logs.length > 0) {
          await supabase.from('deal_assignment_log').insert(logs);
        }
      }

      if (newSdrId === null) {
        toast.success(`${selectedDealIds.length} deals desatribuídos do SDR.`);
      } else {
        const sdrName = sdrUsers.find(u => u.id === newSdrId)?.display_name || 
                        sdrUsers.find(u => u.id === newSdrId)?.first_name || 'SDR';
        toast.success(`${selectedDealIds.length} deals atribuídos para ${sdrName}.`);
      }
      
      clearSelection();
      invalidateCache();
      await loadDeals();
    } catch (e) {
      console.error('Error updating SDR in bulk:', e);
      toast.error(e?.message || 'Erro ao alterar SDR');
    }
  };

  const handleBulkConsultantChange = async (rawConsultantId) => {
    if (selectedDealIds.length === 0) return;
    const newConsultantId = rawConsultantId === '__unassigned__' ? null : rawConsultantId;
    
    try {
      // Get current consultant values for logging
      const { data: currentDeals } = await supabase
        .from('deals')
        .select('id, sales_consultant')
        .in('id', selectedDealIds);

      const { error } = await supabase
        .from('deals')
        .update({ sales_consultant: newConsultantId })
        .in('id', selectedDealIds);
      
      if (error) throw error;

      // Log each assignment change
      if (currentDeals) {
        const logs = currentDeals
          .filter(d => (d.sales_consultant || null) !== newConsultantId)
          .map(d => ({
            deal_id: d.id,
            from_user_id: d.sales_consultant || null,
            to_user_id: newConsultantId,
            target_field: 'sales_consultant',
            reason: 'bulk_reassign',
            changed_by: currentAppUser?.id || null,
          }));
        if (logs.length > 0) {
          await supabase.from('deal_assignment_log').insert(logs);
        }
      }

      if (newConsultantId === null) {
        toast.success(`${selectedDealIds.length} deals desatribuídos do Consultor.`);
      } else {
        const consultantName = consultantUsers.find(u => u.id === newConsultantId)?.display_name || 
                               consultantUsers.find(u => u.id === newConsultantId)?.first_name || 'Consultor';
        toast.success(`${selectedDealIds.length} deals atribuídos para ${consultantName}.`);
      }
      
      clearSelection();
      invalidateCache();
      await loadDeals();
    } catch (e) {
      console.error('Error updating Consultant in bulk:', e);
      toast.error(e?.message || 'Erro ao alterar Consultor');
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedDealIds.length === 0) return;
    
    setIsBulkDeleting(true);
    try {
      const { error } = await supabase
        .from('deals')
        .delete()
        .in('id', selectedDealIds);
      
      if (error) throw error;

      toast.success(`${selectedDealIds.length} deals excluídos com sucesso.`);
      
      clearSelection();
      invalidateCache();
      await loadDeals();
    } catch (e) {
      console.error('Error deleting deals in bulk:', e);
      toast.error(e?.message || 'Erro ao excluir deals');
    } finally {
      setIsBulkDeleting(false);
      setShowBulkDeleteDialog(false);
    }
  };

  // Unified bulk action handler
  const handleBulkAction = (selectedValue) => {
    switch (bulkActionType) {
      case 'stage':
        handleBulkStageChange(selectedValue);
        break;
      case 'sdr':
        handleBulkSdrChange(selectedValue);
        break;
      case 'consultant':
        handleBulkConsultantChange(selectedValue);
        break;
      case 'delete':
        setShowBulkDeleteDialog(true);
        break;
      case 'add_to_list':
      case 'create_list':
        setShowListModal(true);
        break;
      default:
        handleBulkStageChange(selectedValue);
    }
  };

  // Get options based on action type
  const getBulkActionOptions = () => {
    switch (bulkActionType) {
      case 'sdr':
        return [
          { value: '__unassigned__', label: 'Não atribuído' },
          ...sdrUsers.map(u => ({
            value: u.id,
            label: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
          }))
        ];
      case 'consultant':
        return [
          { value: '__unassigned__', label: 'Não atribuído' },
          ...consultantUsers.map(u => ({
            value: u.id,
            label: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
          }))
        ];
      default:
        return [
          { value: 'new', label: 'Novo' },
          { value: 'contact', label: 'Contato' },
          { value: 'scheduled', label: 'Reunião Agendada' },
          { value: 'proposal', label: 'Proposta' },
          { value: 'negotiation', label: 'Negociação' },
          { value: 'won', label: 'Ganho' },
          { value: 'lost', label: 'Perdido' },
        ];
    }
  };

  const getBulkActionPlaceholder = () => {
    switch (bulkActionType) {
      case 'sdr':
        return 'Selecione o SDR...';
      case 'consultant':
        return 'Selecione o Consultor...';
      default:
        return t('deals.selectNewStage');
    }
  };

  const getBulkActionLabel = () => {
    switch (bulkActionType) {
      case 'sdr':
        return 'Atribuir SDR';
      case 'consultant':
        return 'Atribuir Consultor';
      case 'delete':
        return 'Excluir';
      case 'add_to_list':
        return 'Adicionar à Lista';
      case 'create_list':
        return 'Criar Lista';
      default:
        return 'Mover';
    }
  };

  // Dynamic action types based on permissions
  const bulkActionTypes = [
    { value: 'stage', label: 'Mover Etapa' },
    { value: 'sdr', label: 'Alterar SDR' },
    { value: 'consultant', label: 'Alterar Consultor' },
    { value: 'add_to_list', label: 'Adicionar à Lista' },
    { value: 'create_list', label: 'Criar Lista' },
    ...(canBulkDelete ? [{ value: 'delete', label: 'Excluir' }] : [])
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-x-hidden">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 p-3 md:px-6 md:py-2 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 mb-1">
            <div className="relative flex-1">
              <ActiveFilters
                filters={{
                  searchQuery,
                  assignedFilter,
                  dateFilter,
                  kanbanColumnsFilter,
                  sourceFilter,
                  channelFilter,
                  businessUnitFilter,
                  sdrFilter,
                  consultantFilter
                }}
                onRemoveFilter={handleRemoveFilter}
                module="deals"
                appUsers={appUsers}
              />
            </div>

            {!filtersLoaded ? (
              <div className="flex gap-2 items-center">
                <Skeleton className="h-9 w-9 rounded-full" />
                <Skeleton className="h-9 w-24 rounded-md" />
                <Skeleton className="h-9 w-40 rounded-lg" />
                <Skeleton className="h-9 w-9 rounded-full" />
                <Skeleton className="h-9 w-20 rounded-md" />
              </div>
            ) : (
              <div className="flex gap-2 items-center flex-wrap">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowForm(true)}
                  className="rounded-full h-9 w-9 p-0 flex-shrink-0 bg-primary text-white hover:bg-primary/90"
                >
                  <Plus className="w-4 h-4" />
                </Button>

                <Button
                  variant={assignedFilter === 'my_deals' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAssignedFilter(assignedFilter === 'my_deals' ? 'all' : 'my_deals')}
                  className="flex items-center gap-2 h-9"
                >
                  <UserIcon className="w-4 h-4" />
                  {t('deals.myDeals') || 'My Deals'}
                </Button>

                {/* Stage Toggle - Visible in header */}
                <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden h-9">
                  <Button
                    variant={kanbanColumnsFilter === 'ativas' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setKanbanColumnsFilter('ativas')}
                    className="rounded-none h-full px-3 text-xs"
                  >
                    {t('common.active')}
                  </Button>
                  <Button
                    variant={kanbanColumnsFilter === 'nao_ativas' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setKanbanColumnsFilter('nao_ativas')}
                    className="rounded-none h-full px-3 text-xs border-l border-slate-200 dark:border-slate-700"
                  >
                    {t('common.inactive')}
                  </Button>
                  <Button
                    variant={kanbanColumnsFilter === 'all' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setKanbanColumnsFilter('all')}
                    className="rounded-none h-full px-3 text-xs border-l border-slate-200 dark:border-slate-700"
                  >
                    {t('common.all')}
                  </Button>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`rounded-full h-9 w-9 p-0 flex-shrink-0 ${
                    hasActiveFilters
                      ? 'bg-primary text-white hover:bg-primary/90' 
                      : 'hover:bg-slate-200'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                </Button>

                <Button
                  variant={selectionMode ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => {
                    if (selectionMode) {
                      clearSelection();
                    }
                    setSelectionMode(!selectionMode);
                  }}
                  className={`rounded-full h-9 w-9 p-0 flex-shrink-0 ${
                    selectionMode ? '' : 'hover:bg-slate-200'
                  }`}
                >
                  <CheckSquare className="w-4 h-4" />
                </Button>
                
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 whitespace-nowrap text-xs"
                  >
                    {t('common.clearFilters')}
                  </Button>
                )}

              </div>
            )}
          </div>

          <ResponsiveDialog open={showFilters} onOpenChange={setShowFilters} title="Filtros">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">

                {/* Date Filter */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 dark:text-slate-400 mb-2 block">{t('fields.createdAt')}</Label>
                  <Select value={dateFilter} onValueChange={setDateFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('dates.allDates') || 'All dates'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('dates.allDates') || 'All dates'}</SelectItem>
                      <SelectItem value="today">{t('dates.today')}</SelectItem>
                      <SelectItem value="yesterday">{t('dates.yesterday')}</SelectItem>
                      <SelectItem value="this_week">{t('dates.thisWeek') || 'This Week'}</SelectItem>
                      <SelectItem value="this_month">{t('dates.thisMonth') || 'This Month'}</SelectItem>
                      <SelectItem value="custom">{t('dates.selectDateRange') || 'Select Date Range'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Date Range Picker */}
                {dateFilter === "custom" && (
                  <div className="flex-1">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 mb-2 block">{t('dates.dateRange') || 'Date Range'}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {customDateRange?.from ? (
                            customDateRange.to ? (
                              <>
                                {safeFormat(new Date(customDateRange.from), "d MMM", { locale: ptBR })} - {safeFormat(new Date(customDateRange.to), "d MMM", { locale: ptBR })}
                              </>
                            ) : (
                              safeFormat(new Date(customDateRange.from), "d MMM yyyy", { locale: ptBR })
                            )
                          ) : (
                            t('dates.selectDateRange') || "Select date range"
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="range"
                          selected={customDateRange}
                          onSelect={setCustomDateRange}
                          initialFocus
                          numberOfMonths={2}
                          className="pointer-events-auto"
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {/* Sort By */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 dark:text-slate-400 mb-2 block">{t('common.sortBy') || 'Sort By'}</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_date_desc">{t('deals.newestFirst') || 'Newest First'}</SelectItem>
                      <SelectItem value="created_date_asc">{t('deals.oldestFirst') || 'Oldest First'}</SelectItem>
                      <SelectItem value="name_asc">{t('deals.nameAZ') || 'Name (A-Z)'}</SelectItem>
                      <SelectItem value="name_desc">{t('deals.nameZA') || 'Name (Z-A)'}</SelectItem>
                      <SelectItem value="value_desc">{t('deals.valueHighLow') || 'Value (High to Low)'}</SelectItem>
                      <SelectItem value="value_asc">{t('deals.valueLowHigh') || 'Value (Low to High)'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Source Filter */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 mb-2 block">{t('fields.source')}</Label>
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('deals.allSources') || 'All Sources'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('deals.allSources') || 'All Sources'}</SelectItem>
                      {SOURCE_TYPES.map(source => (
                        <SelectItem key={source.value} value={source.value}>
                          {t(source.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Channel Filter */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 mb-2 block">{t('fields.channel')}</Label>
                  <Select value={channelFilter} onValueChange={setChannelFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('contacts.allChannels')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('contacts.allChannels')}</SelectItem>
                      {CHANNEL_TYPES.map(channel => (
                        <SelectItem key={channel.value} value={channel.value}>
                          {t(channel.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Closing Date Filter */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 mb-2 block">{t('fields.closedDate')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {closingDateRange?.from ? (
                          closingDateRange.to ? (
                            <>
                              {new Date(closingDateRange.from).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(closingDateRange.to).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </>
                          ) : (
                            new Date(closingDateRange.from).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          )
                        ) : (
                          <span className="text-muted-foreground">{t('dates.allDates')}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        selected={closingDateRange}
                        onSelect={setClosingDateRange}
                        numberOfMonths={2}
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* SDR Filter - Multi-Select */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 dark:text-slate-400 mb-2 block">SDR</Label>
                  <MultiSelect
                    options={[
                      { value: "unassigned", label: "Sem SDR" },
                      ...sdrUsers.map(u => ({
                        value: u.id,
                        label: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
                      }))
                    ]}
                    value={sdrFilter}
                    onChange={setSdrFilter}
                    placeholder="Todos SDRs"
                    emptyText="Nenhum SDR encontrado"
                    className="w-full"
                  />
                </div>

                {/* Consultant Filter - Multi-Select */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 dark:text-slate-400 mb-2 block">Consultor</Label>
                  <MultiSelect
                    options={[
                      { value: "unassigned", label: "Sem Consultor" },
                      ...consultantUsers.map(u => ({
                        value: u.id,
                        label: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
                      }))
                    ]}
                    value={consultantFilter}
                    onChange={setConsultantFilter}
                    placeholder="Todos Consultores"
                    emptyText="Nenhum consultor encontrado"
                    className="w-full"
                  />
                </div>

                {/* Business Unit Filter */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 dark:text-slate-400 mb-2 block">
                    <div className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      Unidade de Negócio
                    </div>
                  </Label>
                  <BusinessUnitSelector
                    value={businessUnitFilter}
                    onChange={setBusinessUnitFilter}
                    includeAll={true}
                    className="w-full"
                  />
                </div>

                {/* Kanban Columns Filter removed - now visible in header */}

                {/* Action Buttons */}
                <div className="col-span-1 md:col-span-2 flex justify-end gap-2 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setAssignedFilter("all");
                      setDateFilter("all");
                      setCustomDateRange(null);
                      setSortBy("created_date_desc");
                      setKanbanColumnsFilter("all");
                      setSourceFilter("all");
                      setChannelFilter("all");
                      setClosingDateRange(null);
                      setSdrFilter([]);
                      setConsultantFilter([]);
                      setBusinessUnitFilter("all");
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    {t('common.clearFilters')}
                  </Button>
                  <Button size="sm" onClick={() => setShowFilters(false)}>
                    Aplicar
                  </Button>
                </div>
              </div>
          </ResponsiveDialog>
        </div>
      </div>

      {/* Global "Load More" indicator for active/all filters */}
      {/* Legacy load more button - now handled by infinite scroll per stage in Kanban */}

      {/* Scrollable Content Section */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-x-auto overflow-y-auto md:overflow-y-hidden px-4 md:px-6 pb-2">
          <DealKanban
            deals={filteredDeals}
            isSearching={isSearching}
            isLoading={isLoading}
            onStageChange={handleStageChange}
            onViewDeal={handleViewDeal}
            getAssignedUserName={getAssignedUserName}
            kanbanColumnsFilter={kanbanColumnsFilter}
            selectedDealIds={selectionMode ? selectedDealIds : []}
            onToggleSelect={selectionMode ? toggleSelectDeal : undefined}
            scheduledCallsByDeal={scheduledCallsByDeal}
            dealsByStage={dealsByStage}
            stageCounts={stageCounts}
            hasMoreByStage={hasMoreByStage}
            loadingMoreStage={loadingMoreStage}
            onLoadMoreForStage={loadMoreForStage}
            loadingMore={loadingMore}
            stageServerCounts={stageServerCounts}
            hasActiveFilters={hasActiveFilters}
            filteredStageCounts={filteredStageCounts}
          />
        </div>
      </div>

      {/* Deal Form Modal */}
      <Dialog open={showForm} onOpenChange={(open) => {
        setShowForm(open);
        if (!open) {
          setInitialContactId(null);
        }
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('deals.createNewDeal')}</DialogTitle>
          </DialogHeader>
          <DealForm
            onSubmit={handleCreateDeal}
            onCancel={() => {
              setShowForm(false);
              setInitialContactId(null);
            }}
            isProcessing={isProcessing}
            moduleSettings={moduleSettings}
            initialContactId={initialContactId}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedDealIds.length}
        onClearSelection={clearSelection}
        onAction={handleBulkAction}
        itemType="deals"
        placeholder={getBulkActionPlaceholder()}
        options={getBulkActionOptions()}
        actionLabel={getBulkActionLabel()}
        actionTypes={bulkActionTypes}
        actionType={bulkActionType}
        onActionTypeChange={setBulkActionType}
        directActionTypes={['delete', 'add_to_list', 'create_list']}
      />

      {/* List Modal */}
      <ListModal
        open={showListModal}
        onOpenChange={setShowListModal}
        contactIds={selectedDealIds.map(id => deals.find(d => d.id === id)?.contact_id).filter(Boolean)}
        dealIds={selectedDealIds}
        onSuccess={clearSelection}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {selectedDealIds.length} deals?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todos os dados relacionados a esses deals serão perdidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isBulkDeleting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Background loading indicator */}
      {(loadingMore || loadingMoreStage) && (
        <div className="fixed bottom-4 right-4 bg-background border border-border rounded-lg shadow-lg p-3 flex items-center gap-2 z-50">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          <span className="text-sm text-muted-foreground">
            {loadingMoreStage 
              ? `Carregando mais deals (${loadingMoreStage})...`
              : `Carregando mais deals... ${backgroundProgress}%`
            }
          </span>
        </div>
      )}

      {/* Prospecting Validation Modal */}
      <ProspectingValidationModal
        isOpen={showProspectingValidation}
        onClose={() => setShowProspectingValidation(false)}
        dealId={prospectingValidationData.dealId}
        contactId={prospectingValidationData.contactId}
        missingFields={prospectingValidationData.missingFields}
      />
    </div>
  );
}
